import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

// Translations - Chinese default, English available
const translations = {
  zh: {
    // Navbar
    nav: {
      features: '功能特点',
      about: '关于我们',
      pricing: '开始使用',
      contact: '联系我们',
      login: '登录',
      signup: '注册',
      tryDemo: '免费体验画板',
    },
    // Hero Section
    hero: {
      badge: '🎨 AI 驱动的美育评估服务',
      title: '智能绘画过程记录',
      titleHighlight: '让成长看得见',
      subtitle: '记录每一笔的轨迹、节奏与变化，为教学提供过程数据支持。MESA 过程分析，从运笔控制、色彩运用、空间布局、创作投入四维度记录绘画行为。MIND 行为关注，记录创作过程中的行为特征，为教师提供关注参考。',
      getStarted: '立即开始',
      watchDemo: '观看演示',
      trustedBy: '源自',
      schools: '美育测评实践',
    },
    // Stats
    stats: {
      students: '学生测评数据沉淀',
      teachers: '学科专家参与研发',
      artworks: '绘画作品样本',
      accuracy: '专家级行为分析引擎',
    },
    // Features
    features: {
      title: '核心功能',
      subtitle: '全方位艺术教育解决方案',
      items: [
        {
          icon: '🎨',
          title: '智能画布创作',
          desc: '专为平板设计的专业绘画工具，支持压感、图层、多种笔刷',
        },
        {
          icon: '🧠',
          title: '100%数据采集',
          desc: '从运笔控制、色彩运用、空间布局、创作投入四维度记录绘画行为',
        },
        {
          icon: '📊',
          title: 'AI 赋能教科研',
          desc: '实时记录创作轨迹，分析笔触、停顿、修改等行为数据',
        },
        {
          icon: '💭',
          title: 'MIND 行为关注',
          desc: 'MIND 行为关注，记录创作过程中的行为特征，为教师提供关注参考',
        },
        {
          icon: '👨‍🏫',
          title: '教师仪表板',
          desc: '实时分析学生绘画状态，AI赋能生成分析报告',
        },
        {
          icon: '📴',
          title: '离线优先',
          desc: '本地优先架构，无网络也能创作，联网后自动同步',
        },
      ],
    },
    // About / How it works
    about: {
      title: '什么是PPC理论？',
      subtitle: '过程-作品一致性评估框架',
      description: 'AI 智能记录和呈现绘画过程，由博物数字美育实验室研发',
      streamA: {
        title: '结构流 (Stream A)',
        desc: '每30秒保存画布快照，记录作品演变过程',
      },
      streamB: {
        title: '行为流 (Stream B)',
        desc: '实时采集笔触、变换、撤销等操作事件',
      },
      roles: {
        title: '三种用户角色',
        student: {
          title: '学生',
          desc: '使用智能画布进行创作，获得个性化反馈',
        },
        teacher: {
          title: '教师',
          desc: '实时监控班级状态，查看分析报告',
        },
        admin: {
          title: '学校',
          desc: '学校管理，查看班级数据，监控教学质量',
        },
      },
    },
    // CTA
    cta: {
      title: '准备好变革艺术教育了吗？',
      subtitle: '体验智能画板，感受AI驱动的美术教育评估',
      button: '免费体验画板',
      noCreditCard: '',
    },
    // Footer
    footer: {
      product: '产品',
      features: '功能特点',
      pricing: '价格方案',
      demo: '在线演示',
      resources: '资源',
      docs: '帮助文档',
      api: 'API接口',
      blog: '技术博客',
      company: '公司',
      about: '关于我们',
      careers: '加入我们',
      contact: '联系方式',
      copyright: '© 2026 MESAMIND. 博物数字美育实验室（Bowu Lab）研发',
      privacy: '隐私政策',
      terms: '服务条款',
    },
    // Login Modal
    login: {
      title: '欢迎回来',
      signupTitle: '创建账户',
      username: '用户名',
      password: '密码',
      confirmPassword: '确认密码',
      email: '邮箱地址',
      selectRole: '选择角色',
      student: '学生',
      teacher: '教师',
      admin: '管理员',
      loginBtn: '登录',
      signupBtn: '注册',
      noAccount: '还没有账户？',
      hasAccount: '已有账户？',
      switchToSignup: '立即注册',
      switchToLogin: '立即登录',
      forgotPassword: '忘记密码？',
    },
  },
  en: {
    // Navbar
    nav: {
      features: 'Features',
      about: 'About',
      pricing: 'Get Started',
      contact: 'Contact',
      login: 'Login',
      signup: 'Sign Up',
      tryDemo: 'Try Free Canvas',
    },
    // Hero Section
    hero: {
      badge: '🎨 AI-Powered Aesthetic Assessment Service',
      title: 'Smart Drawing Process Recording',
      titleHighlight: 'Making Growth Visible',
      subtitle: 'Record every stroke\'s trajectory, rhythm, and changes, providing process data support for teaching. MESA process analysis records drawing behavior across four dimensions: brush control, color application, spatial layout, and creative engagement. MIND behavioral attention records behavioral characteristics during creation, providing attention references for teachers.',
      getStarted: 'Get Started',
      watchDemo: 'Watch Demo',
      trustedBy: 'Built on',
      schools: 'of Art Education Assessment Practice',
    },
    // Stats
    stats: {
      students: 'Student Assessment Data',
      teachers: 'Domain Experts in R&D',
      artworks: 'Artwork Samples',
      accuracy: 'Expert-Level Analysis Engine',
    },
    // Features
    features: {
      title: 'Core Features',
      subtitle: 'Complete Art Education Solution',
      items: [
        {
          icon: '🎨',
          title: 'Smart Canvas',
          desc: 'Professional drawing tools designed for tablets with pressure sensitivity, layers, and brushes',
        },
        {
          icon: '🧠',
          title: 'MESA Assessment',
          desc: 'Evaluate art literacy across Mastery, Expression, Structure, and Attitude dimensions',
        },
        {
          icon: '📊',
          title: 'Process Analysis',
          desc: 'Real-time tracking of creative trajectory, analyzing strokes, pauses, and modifications',
        },
        {
          icon: '💭',
          title: 'MIND Behavioral Attention',
          desc: 'Identify behavioral fluctuation, creative hesitation, and energy patterns through creative behavior',
        },
        {
          icon: '👨‍🏫',
          title: 'Teacher Dashboard',
          desc: 'Real-time monitoring of class status with one-click detailed analysis reports',
        },
        {
          icon: '📴',
          title: 'Offline First',
          desc: 'Local-first architecture, create without network, auto-sync when connected',
        },
      ],
    },
    // About / How it works
    about: {
      title: 'What is PPC Theory?',
      subtitle: 'Process-Product Consistency Assessment Framework',
      description: 'AI intelligently records and presents the drawing process, developed by the Museum Digital Aesthetic Education Laboratory (Bowu Lab).',
      streamA: {
        title: 'Structure Stream (A)',
        desc: 'Canvas snapshots every 30 seconds, recording artwork evolution',
      },
      streamB: {
        title: 'Behavior Stream (B)',
        desc: 'Real-time collection of strokes, transforms, and undo events',
      },
      roles: {
        title: 'Three User Roles',
        student: {
          title: 'Student',
          desc: 'Create on smart canvas, receive personalized feedback',
        },
        teacher: {
          title: 'Teacher',
          desc: 'Monitor class in real-time, view analysis reports',
        },
        admin: {
          title: 'School',
          desc: 'School management, view class data, monitor teaching quality',
        },
      },
    },
    // CTA
    cta: {
      title: 'Ready to Transform Art Education?',
      subtitle: 'Try the smart canvas and experience AI-powered art education assessment',
      button: 'Try Free Canvas',
      noCreditCard: '',
    },
    // Footer
    footer: {
      product: 'Product',
      features: 'Features',
      pricing: 'Pricing',
      demo: 'Live Demo',
      resources: 'Resources',
      docs: 'Documentation',
      api: 'API Reference',
      blog: 'Tech Blog',
      company: 'Company',
      about: 'About Us',
      careers: 'Careers',
      contact: 'Contact',
      copyright: '© 2026 MESAMIND. Developed by Bowu Lab',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    // Login Modal
    login: {
      title: 'Welcome Back',
      signupTitle: 'Create Account',
      username: 'Username',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      email: 'Email Address',
      selectRole: 'Select Role',
      student: 'Student',
      teacher: 'Teacher',
      admin: 'Admin',
      loginBtn: 'Login',
      signupBtn: 'Sign Up',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      switchToSignup: 'Sign up now',
      switchToLogin: 'Login now',
      forgotPassword: 'Forgot password?',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('zh'); // Chinese default

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'zh' ? 'en' : 'zh');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
